'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const relatedServices = [
  { label: 'Container Transport', href: '/container-transport' },
  { label: 'Less Container Load (LCL)', href: '/lcl-transport' },
  { label: 'Airfreight Transport', href: '/airfreight-transport' },
  { label: 'Inland Transport', href: '/inland-transport' },
  { label: 'Customs Representation', href: '/customs-representation' },
  { label: 'Cargo Insurance', href: '/cargo-insurance' },
  { label: 'Bank Guarantee', href: '/bank-guarantee' },
];

export default function PortOperationsPage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F4F6F9' }}>
      <Header />
      <main className="flex-1 pt-32 pb-20">
        {/* Hero section */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Illustration */}
            <div className="flex items-center justify-center">
              <svg viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-sm lg:max-w-md" aria-label="Port crane and container operations illustration">
                <rect x="148" y="30" width="16" height="180" rx="4" stroke="#1B6B8A" strokeWidth="4" fill="rgba(27,107,138,0.06)"/>
                <rect x="60" y="30" width="200" height="14" rx="4" stroke="#1B6B8A" strokeWidth="3.5" fill="rgba(27,107,138,0.06)"/>
                <line x1="100" y1="44" x2="100" y2="120" stroke="#1B6B8A" strokeWidth="2.5" strokeDasharray="6 3"/>
                <rect x="72" y="120" width="56" height="36" rx="4" stroke="#1B6B8A" strokeWidth="3.5" fill="rgba(27,107,138,0.08)"/>
                <line x1="72" y1="138" x2="128" y2="138" stroke="#1B6B8A" strokeWidth="2"/>
                <line x1="100" y1="120" x2="100" y2="156" stroke="#1B6B8A" strokeWidth="2"/>
                <rect x="30" y="195" width="60" height="36" rx="4" stroke="#1B6B8A" strokeWidth="3.5" fill="rgba(27,107,138,0.06)"/>
                <line x1="30" y1="213" x2="90" y2="213" stroke="#1B6B8A" strokeWidth="2"/>
                <rect x="100" y="195" width="60" height="36" rx="4" stroke="#1B6B8A" strokeWidth="3.5" fill="rgba(27,107,138,0.04)"/>
                <line x1="100" y1="213" x2="160" y2="213" stroke="#1B6B8A" strokeWidth="2"/>
                <rect x="170" y="195" width="60" height="36" rx="4" stroke="#1B6B8A" strokeWidth="3.5" fill="rgba(27,107,138,0.06)"/>
                <line x1="170" y1="213" x2="230" y2="213" stroke="#1B6B8A" strokeWidth="2"/>
                <rect x="30" y="158" width="60" height="36" rx="4" stroke="#1B6B8A" strokeWidth="3" fill="rgba(27,107,138,0.04)"/>
                <rect x="170" y="158" width="60" height="36" rx="4" stroke="#1B6B8A" strokeWidth="3" fill="rgba(27,107,138,0.04)"/>
                <line x1="20" y1="232" x2="300" y2="232" stroke="#2A8FAB" strokeWidth="3" strokeLinecap="round"/>
                <path d="M230 240 Q250 234 270 240 Q290 246 300 240" stroke="#7EC8DC" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                <line x1="148" y1="210" x2="120" y2="232" stroke="#1B6B8A" strokeWidth="3" strokeLinecap="round"/>
                <line x1="164" y1="210" x2="192" y2="232" stroke="#1B6B8A" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>

            {/* Text */}
            <div>
              <div className="w-10 h-1 mb-5 rounded" style={{ background: '#1B6B8A' }} />
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: '#0F2340', fontFamily: 'var(--font-display, sans-serif)' }}>
                Port operations
              </h1>
              <div className="space-y-5 text-base lg:text-lg leading-relaxed" style={{ color: '#3A5070' }}>
                <p>World Transport Overseas delivers comprehensive <strong style={{ color: '#0F2340' }}>port operations services</strong> at major ports, combining stevedoring expertise with modern cargo handling equipment. Our experienced teams manage the loading and unloading of vessels efficiently, minimising turnaround times and reducing port costs for our clients.</p>
                <p>From container terminal services and bulk cargo handling to breakbulk and project cargo operations, WTO has the capability and experience to handle all cargo types. Our secure port storage facilities — both covered warehousing and open yard — provide flexible solutions for short and long-term cargo storage requirements.</p>
                <p>Our port agency services provide vessels with comprehensive support including clearance with port authorities, crew assistance, husbandry services, and coordination of all port-related activities. WTO's port operations team works around the clock to ensure smooth and efficient vessel turnarounds at every port we serve.</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link href="/transport-quote" className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-semibold text-sm transition-all duration-200 hover:opacity-90 shadow-md" style={{ background: '#1A5FAB' }}>
                  Get a Quote
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key highlights */}
        <section className="py-14" style={{ background: '#0F2340' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Port Operations Capabilities</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'Stevedoring', desc: 'Professional loading and unloading of vessels at major ports with modern equipment' },
                { title: 'Cargo Storage', desc: 'Secure warehousing and open yard storage facilities at key port locations' },
                { title: 'Cargo Handling', desc: 'Specialist handling for all cargo types including bulk, breakbulk, and project cargo' },
                { title: 'Port Agency', desc: 'Comprehensive port agency services including vessel clearance and crew assistance' },
              ]?.map((h, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="w-8 h-8 rounded-lg mb-4 flex items-center justify-center" style={{ background: 'rgba(126,200,220,0.2)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#7EC8DC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9 12l2 2 4-4M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.66 0 3.21.45 4.55 1.23" /></svg>
                  </div>
                  <p className="font-bold text-base mb-1" style={{ color: '#FFFFFF' }}>{h?.title}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{h?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services list */}
        <section className="py-14" style={{ background: '#0A1628' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Port Services</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { title: 'Vessel Loading & Discharging', desc: 'Efficient stevedoring operations for all vessel types using modern cranes and handling equipment.' },
                { title: 'Container Terminal Services', desc: 'Full container handling including stuffing, stripping, and container inspection services.' },
                { title: 'Bulk Cargo Handling', desc: 'Specialist equipment and expertise for dry and liquid bulk cargo operations.' },
                { title: 'Breakbulk & Project Cargo', desc: 'Handling of oversized, heavy-lift, and project cargo requiring specialist equipment.' },
                { title: 'Port Storage & Warehousing', desc: 'Secure covered and open storage facilities with inventory management systems.' },
                { title: 'Port Agency Services', desc: 'Vessel clearance, crew assistance, husbandry services, and port authority liaison.' },
              ]?.map((c, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <p className="font-bold text-base mb-2" style={{ color: '#FFFFFF' }}>{c?.title}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{c?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-14" style={{ background: '#0E1F38' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Other Services</h2>
            <div className="flex flex-wrap gap-3">
              {relatedServices?.map((s) => (
                <Link key={s?.href} href={s?.href} className="px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 hover:opacity-80" style={{ background: '#132848', color: '#B8CDE0', border: '1px solid rgba(255,255,255,0.15)' }}>
                  {s?.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
